/**
 * BufferedMoneyField - Money/currency input field
 *
 * Uses centralized styling from styles/utilities/view-agreement.ts
 * for consistent field heights and appearance.
 */

import { TextField, useTheme } from '@mui/material'
import * as React from 'react'
import { toMoney } from 'utils/toMoney'
import { productMoneyFieldSx } from 'styles/utilities/view-agreement'

type Props = {
  label: string
  value: number | string | null | undefined
  disabled?: boolean
  error?: boolean
  onCommit: (nextNumber: number) => void
  width?: number | string
}

export const BufferedMoneyField = React.memo(function BufferedMoneyField({
  label,
  value,
  disabled,
  error,
  onCommit,
}: Props) {
  const theme = useTheme()
  const [local, setLocal] = React.useState<string>(value == null ? '' : String(value))
  const focusedRef = React.useRef(false)

  // Keep in sync if outer value changes (but don't fight the user while typing)
  React.useEffect(() => {
    if (!focusedRef.current) setLocal(value == null ? '' : String(value))
  }, [value])

  // Get centralized styling
  const fieldStyles = productMoneyFieldSx(theme)

  return (
    <TextField
      type="text"
      inputMode="decimal"
      fullWidth
      size="small"
      label={label}
      variant="outlined"
      value={local}
      onFocus={() => (focusedRef.current = true)}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        focusedRef.current = false
        onCommit(toMoney(local))
      }}
      disabled={disabled}
      error={error}
      inputProps={{
        step: '0.01',
        inputMode: 'decimal',
        pattern: '\\d{1,}(\\.\\d{0,2})?',
      }}
      sx={fieldStyles}
    />
  )
})
